/**
 * PhoneView - Mobile display for HTML content from GameModeSDK.
 *
 * ARCHITECTURE: View component (src/views/)
 * - NO Socket.io connection management (done by parent Page)
 * - Uses hooks (useGameDisplay) that assume socket is already connected
 * - Reads data from stores (gameStore.phoneInfo, gameStore.currentQuestion)
 * - Renders HTML emitted by backend GameModeSDK
 *
 * Parent: GamePage (src/pages/GamePage.tsx) calls useSocket()
 *
 * PHASE 8: Renders content emitted by backend GameMode:
 * - info: Upper zone (game information, cards, status)
 * - actions: Lower zone (action panel for player input)
 *
 * All HTML in info area is sanitized with DOMPurify before rendering.
 *
 * Layout:
 * ```
 * ┌──────────────────────┐
 * │   Phone Header       │
 * ├──────────────────────┤
 * │                      │  50% (info area), or less
 * │   Game Info / Card   │  only when it holds nothing
 * │  (HTML from SDK)     │  but a status line
 * │                      │
 * ├──────────────────────┤
 * │ Action Panel Buttons │  50% (action area), more
 * │     (User Input)     │  only in that same case
 * └──────────────────────┘
 * ```
 *
 * The action panel never exceeds half of the area below the header. The single
 * exception, resolved by useAdaptiveSplit: an info zone that is empty or holds
 * no more than a single status line.
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { useGameStore } from '@/stores/gameStore'
import { useGameDisplay } from '@/hooks/useGameDisplay'
import { useQuestionEvents } from '@/hooks/useQuestionEvents'
import { useNotifications } from '@/hooks/useNotifications'
import { useGameLifecycle } from '@/hooks/useGameLifecycle'
import { useAudio } from '@/hooks/useAudio'
import { useMediaSession } from '@/hooks/useMediaSession'
import { useTtsCaptions } from '@/hooks/useTtsCaptions'
import { useAnimatedEmoji } from '@/hooks/useAnimatedEmoji'
import { useAdaptiveSplit } from '@/hooks/useAdaptiveSplit'
import { usePlayerStore } from '@/stores/playerStore'
import DOMPurify from 'dompurify'
import { useHtmlActionBridge } from '@/hooks/useHtmlActionBridge'
import { ActionPanel } from '@/components/medium/ActionPanel'
import { MultiSelectPanel } from '@/components/medium/MultiSelectPanel'
import { FormPanel } from '@/components/medium/FormPanel'
import { GridPanel } from '@/components/medium/GridPanel'
import { AudioIndicator } from '@/components/high/AudioIndicator'
import { LoadingOverlay } from '@/components/low/LoadingOverlay'
import { submitResponse } from '@/services/api'
import type { QuestionPanelDefinition, MultiSelectPanelDefinition, ActionPanelDefinition, FormPanelDefinition } from '@/types/actions'
import { isGridPanel } from '@/types/actions'
import { PaywallModal } from '@/components/high/billing/PaywallModal'
import { PixelIcon } from '@/components/low/PixelIcon'

/**
 * PhoneView component.
 *
 * Adaptive Sci-Fi Controller Layout:
 * 1. HUD Header: Navigation and status
 * 2. Info area (flexible): Displays HTML content from GameModeSDK
 * 3. Actions area (capped at half the area, see file header): Displays action
 *    buttons for player responses
 */
export function PhoneView() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  // Register event listeners
  useGameDisplay()
  useQuestionEvents()
  useNotifications()
  useGameLifecycle()
  useAudio()
  useMediaSession()
  useTtsCaptions()

  const phoneInfo = useGameStore((state) => state.phoneInfo)
  const phoneInfoUnsafe = useGameStore((state) => state.phoneInfoUnsafe)
  const loadingOverlay = useGameStore((state) => state.loadingOverlay)
  const currentQuestion = useGameStore((state) => state.currentQuestion)
  const paywallData = useGameStore((state) => state.paywallData)
  const clearPaywallData = useGameStore((state) => state.clearPaywallData)
  const sessionCode = usePlayerStore((state) => state.sessionCode)
  const playerId = usePlayerStore((state) => state.playerId)
  
  const infoContainerRef = useRef<HTMLDivElement | null>(null)
  const contentAreaRef = useRef<HTMLDivElement>(null)
  const lastHtmlRef = useRef<string>('')
  const [renderedHtml, setRenderedHtml] = useState<string>('')

  // Adaptive split: caps the action panel so it never squeezes the info zone
  // below half of the screen, while still allowing it to take more when the
  // info zone is empty or holds a single status line.
  const { panelMaxHeight, remeasure } = useAdaptiveSplit(
    contentAreaRef,
    infoContainerRef,
    renderedHtml,
  )

  // Type guards
  const isMultiSelectPanel = (panel: QuestionPanelDefinition): panel is MultiSelectPanelDefinition => {
    return panel.panel_type === 'multiselect'
  }

  const isActionPanel = (panel: QuestionPanelDefinition): panel is ActionPanelDefinition => {
    return panel.panel_type === 'action'
  }

  const isFormPanel = (panel: QuestionPanelDefinition): panel is FormPanelDefinition => {
    return panel.panel_type === 'form'
  }

  // Handler for action panel click.
  // Throws on every failure path — the callers (ActionPanel, GridPanel, HTML
  // bridge) signal success to the player purely by this promise resolving, so a
  // silent return would confirm an answer that was never sent.
  const handleActionClick = async (actionId: string) => {
    if (!playerId || !sessionCode || !currentQuestion) {
      console.error('[PhoneView] Missing required data for action submit')
      throw new Error(i18next.t('game.missingSessionOrPlayer'))
    }

    // A form is answerable this way only through an action_grid field: the cell
    // replies with an ActionPanelResponse even though the panel is a form. Any
    // other form expects field_values, so an action response would be dropped.
    const panel = currentQuestion.panel
    const isActionGridForm =
      panel.panel_type === 'form' &&
      panel.fields.some((field) => field.field_type === 'action_grid')
    if (panel.panel_type !== 'action' && panel.panel_type !== 'grid' && !isActionGridForm) {
      console.error(
        `[PhoneView] panel_type mismatch: expected action, grid or action_grid form, got ${panel.panel_type}`
      )
      throw new Error(i18next.t('game.actionNotAvailable'))
    }

    try {
      // Find the action to get target_id from target_options
      const action = isActionPanel(panel)
        ? panel.actions.find(a => a.id === actionId)
        : isGridPanel(panel)
          ? panel.cells.find(cell => cell.action.id === actionId)?.action
          : undefined
      const targetId = action?.target_options?.[0]?.id ?? undefined

      await submitResponse(sessionCode, {
        response_type: 'action',
        action_id: actionId,
        player_id: playerId,
        request_id: currentQuestion.requestId,
        target_id: targetId,
        timestamp: Date.now(),
      })
    } catch (err) {
      console.error('[PhoneView] Failed to submit action response:', err)
      throw err // Re-throw for ActionPanel to handle
    }
  }

  // HTML bridge: clicks on data-action-id elements inside the game-mode HTML
  // submit panel actions. Only panels that opted in (display_mode) participate.
  const panel = currentQuestion?.panel
  const bridgeEnabled =
    (panel?.panel_type === 'action' || panel?.panel_type === 'grid') &&
    panel.display_mode === 'html_bridge'

  const allowedActionIds = useMemo(() => {
    if (!panel) return new Set<string>()
    if (panel.panel_type === 'action') return new Set(panel.actions.map((a) => a.id))
    if (panel.panel_type === 'grid') return new Set(panel.cells.map((c) => c.action.id))
    return new Set<string>()
  }, [panel])

  const bridgedActionIds = useHtmlActionBridge({
    containerRef: infoContainerRef,
    allowedActionIds,
    enabled: bridgeEnabled,
    onSubmit: handleActionClick,
    html: renderedHtml,
  })

  // Handler for multiselect submit
  const handleMultiSelectSubmit = async (selectedIds: string[]) => {
    if (!playerId || !sessionCode || !currentQuestion) {
      console.error('[PhoneView] Missing required data for multiselect submit')
      return
    }

    if (currentQuestion.panel.panel_type !== 'multiselect') {
      console.error(`[PhoneView] panel_type mismatch: expected multiselect, got ${currentQuestion.panel.panel_type}`)
      return
    }

    try {
      await submitResponse(sessionCode, {
        response_type: 'multiselect',
        player_id: playerId,
        request_id: currentQuestion.requestId,
        selected_ids: selectedIds,
        timestamp: Date.now(),
      })
    } catch (err) {
      console.error('[PhoneView] Failed to submit multiselect response:', err)
      throw err // Re-throw for MultiSelectPanel to handle
    }
  }

  // Handler for form submit
  const handleFormSubmit = async (fieldValues: Record<string, string | string[]>) => {
    if (!playerId || !sessionCode || !currentQuestion) {
      console.error('[PhoneView] Missing required data for form submit')
      return
    }

    if (currentQuestion.panel.panel_type !== 'form') {
      console.error(`[PhoneView] panel_type mismatch: expected form, got ${currentQuestion.panel.panel_type}`)
      return
    }

    try {
      await submitResponse(sessionCode, {
        response_type: 'form',
        player_id: playerId,
        request_id: currentQuestion.requestId,
        field_values: fieldValues,
        timestamp: Date.now(),
      })
    } catch (err) {
      console.error('[PhoneView] Failed to submit form response:', err)
      throw err
    }
  }

  // Memoize sanitized HTML to prevent unnecessary re-renders
  const processedHtml = useMemo(() => {
    if (!phoneInfo) return ''
    return phoneInfoUnsafe ? phoneInfo : DOMPurify.sanitize(phoneInfo)
  }, [phoneInfo, phoneInfoUnsafe])

  // Only update DOM if HTML actually changed (prevents flicker on identical updates)
  useEffect(() => {
    if (processedHtml && processedHtml !== lastHtmlRef.current) {
      lastHtmlRef.current = processedHtml
      setRenderedHtml(processedHtml)
    }
  }, [processedHtml])

  // Animate emoji in sanitized info HTML. Unsafe HTML is excluded: its scripts inspect
  // textContent and selectors, which the emoji replacement would change under them.
  useAnimatedEmoji(infoContainerRef, phoneInfoUnsafe ? null : renderedHtml)

  // Game-mode HTML is injected imperatively instead of through
  // dangerouslySetInnerHTML, and the injection is bound to the DOM node itself.
  // Scripts parsed out of innerHTML are inert, so they have to be re-created to
  // run — and that must happen on every attach of the node, not only when the
  // HTML string changes. React remounts this container while renderedHtml stays
  // equal (e.g. when the action panel appears), which re-parses the markup into a
  // fresh inert copy; keying script execution off the HTML value alone left the
  // mode's card as a dead skeleton for the rest of the game.
  const attachInfoContainer = useCallback(
    (node: HTMLDivElement | null) => {
      infoContainerRef.current = node
      if (!node) return

      node.innerHTML = renderedHtml

      if (phoneInfoUnsafe) {
        node.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script')
          Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value)
          })
          newScript.textContent = oldScript.textContent
          oldScript.parentNode?.replaceChild(newScript, oldScript)
        })
      }

      // Mode scripts build their DOM deep inside the container, which no observer
      // watching direct children reports — measure once they have run.
      remeasure()
    },
    [renderedHtml, phoneInfoUnsafe, remeasure],
  )

  return (
    <div className="h-[100dvh] bg-background text-foreground flex flex-col overflow-hidden font-sans selection:bg-primary/20">
      {/* HUD Header */}
      <header className="flex-shrink-0 bg-card border-b-2 border-pixel-outline px-3 py-2.5 flex items-center justify-between relative z-20">
        {/* Left: Home */}
        <button
          onClick={() => navigate('/')}
          className="p-2 hover:bg-accent rounded-none transition-none"
          aria-label={t("nav.homeAriaLabel")}
        >
          <PixelIcon className="text-lg">🏠</PixelIcon>
        </button>

        {/* Center: Session code */}
        <div className="flex flex-col items-center">
          {/* font-display, not the arcade face: Press Start 2P below ~12px is
              unreadable, and this is a functional label rather than decor. */}
          <span className="font-display text-[11px] text-muted-foreground uppercase tracking-widest leading-none mb-0.5">{t("nav.sessionLabel")}</span>
          <span className="font-mono text-sm font-bold text-secondary tracking-wider bg-muted px-3 py-0.5 rounded-none border-2 border-pixel-outline">
            {sessionCode || '------'}
          </span>
        </div>

        {/* Right: Audio + Settings */}
        <div className="flex items-center gap-1">
          <AudioIndicator variant="phone" />
          <button
            className="p-2 hover:bg-accent rounded-none transition-none"
            aria-label={t("nav.settingsAriaLabel")}
          >
            <PixelIcon className="text-lg">⚙️</PixelIcon>
          </button>
        </div>
      </header>

      {/* Main Content Area - Adaptive Flex */}
      <div ref={contentAreaRef} className="flex-grow flex flex-col overflow-hidden relative">
        {/* Info Area - Fills remaining space, game mode controls scroll */}
        <div className="flex-1 min-h-0 overflow-hidden p-4 relative z-10">
          {renderedHtml ? (
            <div
              ref={attachInfoContainer}
              className="prose-invert prose-sm max-w-none h-full flex flex-col overflow-y-auto"
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground space-y-4">
              <div className="w-12 h-12 border-2 border-muted border-t-primary rounded-full animate-spin" />
              <p className="text-muted-foreground text-xs">{t("game.awaitingDataStream")}</p>
            </div>
          )}
        </div>

        {/* Action Panel - capped at half the content area; the panel card scrolls
            internally so its border and rounded corners stay visible. The static
            max-h-[50%] is the floor that applies before the first measurement;
            the inline style overrides it once a measurement exists. */}
        {currentQuestion && (
          <div
            className="flex-shrink-0 relative z-20 flex flex-col min-h-0 overflow-hidden max-h-[50%]"
            style={panelMaxHeight > 0 ? { maxHeight: `${panelMaxHeight}px` } : undefined}
          >
            {isFormPanel(currentQuestion.panel) ? (
              <FormPanel
                panel={currentQuestion.panel}
                requestId={currentQuestion.requestId}
                onSubmit={handleFormSubmit}
                onActionClick={handleActionClick}
                onTimeout={() => useGameStore.getState().clearCurrentQuestion()}
              />
            ) : isMultiSelectPanel(currentQuestion.panel) ? (
              <MultiSelectPanel
                panel={currentQuestion.panel}
                requestId={currentQuestion.requestId}
                onSubmit={handleMultiSelectSubmit}
              />
            ) : isGridPanel(currentQuestion.panel) ? (
              <GridPanel
                panel={currentQuestion.panel}
                requestId={currentQuestion.requestId}
                onActionClick={handleActionClick}
                bridgedActionIds={bridgedActionIds}
              />
            ) : isActionPanel(currentQuestion.panel) ? (
              <ActionPanel
                panel={currentQuestion.panel}
                requestId={currentQuestion.requestId}
                onActionClick={handleActionClick}
                bridgedActionIds={bridgedActionIds}
              />
            ) : (
              (() => {
                console.error(`[PhoneView] Unknown panel_type: ${(currentQuestion.panel as { panel_type: string }).panel_type}`)
                return null
              })()
            )}
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {loadingOverlay?.visible && (
        <LoadingOverlay messages={loadingOverlay.messages} />
      )}

      {/* In-game Paywall Overlay — host only (billing:paywall_required) */}
      {paywallData && (
        <PaywallModal
          open={true}
          onClose={clearPaywallData}
          trigger="in_game_cutoff"
          plans={paywallData.plans}
          message={paywallData.message}
        />
      )}
    </div>
  )
}
